import type { BlocklistCause, BlocklistEntry } from '@bartizo/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formatRelative } from '../lib/time.js';
import { ServerLinks } from './ServerLinks.js';

/**
 * Why an address is blocked, in the words of what banned it (P11.16): a CrowdSec
 * trigger rule, a behaviour rule, one of CrowdSec's own scenarios, a person on a
 * server, this console — or a person's entry on the list itself. Each says which, so
 * "who decided this?" is never a question.
 */

const KIND_LABEL: Readonly<Record<BlocklistCause['kind'], string>> = {
  trigger: 'Trigger rule',
  behaviour: 'Behaviour rule',
  scenario: 'CrowdSec scenario',
  person: 'A person',
  console: 'This console',
  other: 'Other',
};

const KIND_TONE: Readonly<Record<BlocklistCause['kind'], string>> = {
  trigger: 'badge-accent',
  behaviour: 'badge-accent',
  scenario: 'badge-muted',
  person: 'badge-warning',
  console: 'badge-warning',
  other: 'badge-muted',
};

/** `crowdsecurity/ssh-bf` → `ssh-bf`: the part a person recognises. */
const short = (name: string): string => name.slice(name.indexOf('/') + 1);

const causeText = (cause: BlocklistCause): string =>
  cause.kind === 'scenario' ? short(cause.label) : (cause.ruleName ?? cause.label);

/** One line in a table cell: the main cause and how many others. */
export function CauseBadges({ entry }: { entry: BlocklistEntry }): ReactNode {
  if (entry.source === 'manual') {
    return (
      <>
        <span className="badge badge-warning">Added by a person</span>
        <span className="cell-sub">
          {entry.addedByEmail ?? 'someone'}: {entry.reason}
        </span>
      </>
    );
  }
  const [first, ...others] = entry.causes;
  if (first === undefined) {
    return (
      <>
        <span className="badge badge-muted">The policy</span>
        <span className="cell-sub">{entry.reason}</span>
      </>
    );
  }
  return (
    <>
      <span className="badge-list">
        <span className={`badge ${KIND_TONE[first.kind]}`} title={first.scenario ?? undefined}>
          {KIND_LABEL[first.kind]}
        </span>
        <span className="cause-name">{causeText(first)}</span>
        {others.length > 0 && (
          <span className="muted text-xs" title={others.map(causeText).join(', ')}>
            +{others.length} more
          </span>
        )}
      </span>
      <span className="cell-sub">
        {entry.bans} ban{entry.bans === 1 ? '' : 's'} on {entry.servers} server
        {entry.servers === 1 ? '' : 's'}
        {others.length === 0 && first.kind === 'scenario' && ' · CrowdSec’s own, no Bartizo rule'}
        {others.length === 0 && (first.kind === 'trigger' || first.kind === 'behaviour') && (
          <>
            {' · '}
            <Link to="/rules">See the rule</Link>
          </>
        )}
      </span>
    </>
  );
}

/** Every cause, with how often, where, and when last, and a way to the rule. */
export function CauseList({ entry }: { entry: BlocklistEntry }): ReactNode {
  return (
    <>
      {entry.source === 'manual' && (
        <p className="text-sm">
          <span className="badge badge-warning">Added by a person</span>{' '}
          {entry.addedByEmail ?? 'someone'}: {entry.reason}
        </p>
      )}
      {entry.causes.length === 0 ? (
        <p className="text-sm muted">
          {entry.source === 'manual'
            ? 'No server has banned it on its own.'
            : 'No ban on record names a cause; the policy put it here.'}
        </p>
      ) : (
        <ul className="cause-list">
          {entry.causes.map((cause) => (
            <li key={`${cause.kind}-${cause.label}`}>
              <span className={`badge ${KIND_TONE[cause.kind]}`}>{KIND_LABEL[cause.kind]}</span>{' '}
              <strong>{cause.kind === 'scenario' ? cause.label : causeText(cause)}</strong>
              {cause.ruleName !== null && <span className="cell-sub">rule id {cause.label}</span>}
              {cause.scenario !== null && cause.kind !== 'scenario' && (
                <span className="cell-sub">scenario {cause.scenario}</span>
              )}
              <span className="cell-sub">
                {cause.bans} ban{cause.bans === 1 ? '' : 's'} on{' '}
                {cause.serverRefs.length === 0 ? (
                  `${String(cause.servers)} server${cause.servers === 1 ? '' : 's'}`
                ) : (
                  <ServerLinks servers={cause.serverRefs} count={cause.servers} />
                )}{' '}
                · last {formatRelative(cause.lastSeen)}
                {(cause.kind === 'trigger' || cause.kind === 'behaviour') && (
                  <>
                    {' · '}
                    <Link to="/rules">See the rule</Link>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
